import * as React from 'react';
import { useLocale, usePermissions } from 'react-admin';
import { Paper } from '@material-ui/core';
import { CountryAutocomplete, MaterialTable } from '~/components';
import i18n from '~/i18n';
import CountryDetails from './CountryDetails';
import * as hb from '~/types/Handbook'; // eslint-disable-line @typescript-eslint/no-unused-vars
import { validateUnique, createHelperText } from './utils';

const CountryTable = ({
  handbook,
  setState,
}: {
  handbook: Partial<hb.Handbook>;
  setState: hb.SetState;
}) => {
  const locale: keyof any = useLocale();
  const allCountries: { [key: string]: string } = (i18n as any)[locale].countries;

  const countries: hb.Country[] = (handbook.countries || []).slice();

  const setCountry: hb.SetCountry = (country: hb.Country) => {
    const index = countries.findIndex((c) => c.id === country.id);
    if (index >= 0) {
      countries[index] = country;
      return setState({ countries });
    } else {
      return Promise.reject();
    }
  };

  const { permissions } = usePermissions();
  if (!permissions) return null;

  return (
    <MaterialTable
      columns={[
        {
          title: 'Country',
          field: 'id',
          required: true,
          validate: validateUnique(countries, 'id', true),
          render: (rowData: hb.Country) => `${allCountries[rowData.id]} (${rowData.id})`,
          cellStyle: {
            minWidth: 500,
          },
        },
      ]}
      components={{
        Container: (props: any) => <Paper variant='outlined'>{props.children}</Paper>,
        EditField: (props: any) => {
          const errorMessage = props.columnDef.validate
            ? props.columnDef.validate(props.rowData)
            : null;
          const helperText = createHelperText(errorMessage);
          return (
            <CountryAutocomplete
              value={props.value || ''}
              label={props.columnDef.title}
              onChange={(event) => {
                props.onChange(event.target.value);
              }}
              required={props.columnDef.required}
              helperText={helperText}
              error={errorMessage?.length > 0}
            />
          );
        },
      }}
      data={handbook.countries}
      editable={
        permissions?.includes('solution.handbook.update') && {
          onRowAdd: (newCountry: hb.Country) => {
            newCountry.name = allCountries[newCountry.id];
            newCountry.nameLocalized = newCountry.name; // FIXME add localized name
            countries.push(newCountry);
            return setState({ countries });
          },
          onRowUpdate: (newCountry: hb.Country, oldCountry: hb.Country) => {
            newCountry.name = allCountries[newCountry.id];
            newCountry.nameLocalized = newCountry.name; // FIXME add localized name
            const index = countries.indexOf(oldCountry);
            countries[index] = newCountry;
            return setState({ countries });
          },
          onRowDelete: (oldRecord: hb.Country) => {
            const index = countries.indexOf(oldRecord);
            countries.splice(index, 1);
            return setState({ countries });
          },
        }
      }
      detailPanel={(rowData: hb.Country) => {
        return <CountryDetails country={rowData} setCountry={setCountry} />;
      }}
      onRowClick={(event: any, rowData: hb.Country, togglePanel: () => void) => togglePanel()}
      options={{
        actionsColumnIndex: -1,
        emptyRowsWhenPaging: false,
        showTitle: false,
        paging: false,
        addRowPosition: 'first',
      }}
    />
  );
};

export default CountryTable;
